import { DOCUMENT } from '@angular/common';
import { DestroyRef, Inject, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  IconoirCurveArray,
  IconoirFlash,
  IconoirHomeSimple,
  IconoirInfoCircle,
  IconoirNetworkReverse,
  IconoirPriorityUp,
  IconoirSearch,
  IconoirStatsReport,
} from '@ey-xd/motif-icon';
import { ModalSizes, MotifModal, MotifModalConfig } from '@ey-xd/ng-motif';
import {
  APP_EVENT,
  APP_EVENT_KEY,
  APP_TIMEOUT_SETTING_KEY,
  APP_WARNING_TIMEOUT_SETTING_KEY,
  DEFAULT_APP_TIMEOUT,
  DEFAULT_APP_WARNING_TIMEOUT,
  NavigationItem,
  THEME,
} from '@models';
import { PrivacyNoticeDto } from '@models/webapi/privacy-notice/privacy-notice.model';
import { CompanyContext } from '@models/webapi/user-profile';
import {
  AUDIT_HISTORY_ROLES,
  DASHBOARD_ROLES,
  DISPUTE_REQUESTS_ROLES,
  EXPEDITE_REQUESTS_ROLES,
  INFORMATION_REQUESTS_ROLES,
  REQUESTS_ROLES,
  ROUTES,
  SEARCH_ROLES,
} from '@routes';
import { EventService, IdleService, LocalStorageService } from '@services';
import { EytcAuthService } from '@services/authentication/eytc-auth.service';
import { PrivacyNoticeService } from '@services/webapi/privacy-notice.service';
import { SettingsService } from '@services/webapi/settings.service';
import { LogoutWarningModalComponent, PrivacyPolicyComponent, PrivacyPolicyComponentData } from '@views';
import { combineLatest, merge, Observable } from 'rxjs';
import { filter, map, pairwise, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MainHelperService {
  public lightQuery = window.matchMedia('(prefers-color-scheme: light)');
  private listener?: (e: MediaQueryListEvent) => void;
  public readonly modal: MotifModal = inject(MotifModal);
  public readonly mainLinks: NavigationItem[] = [
    {
      icon: IconoirHomeSimple,
      label: 'Home',
      route: ROUTES.FULL_HOME_ROUTE,
      exactRoute: true,
    },
    {
      icon: IconoirStatsReport,
      label: 'Dashboard',
      route: ROUTES.FULL_DASHBOARD_ROUTE,
      exactRoute: true,
      roles: DASHBOARD_ROLES,
    },
    {
      icon: IconoirSearch,
      label: 'Product Search',
      route: ROUTES.FULL_SEARCH_ROUTE,
      exactRoute: true,
      roles: SEARCH_ROLES,
    },
    {
      icon: IconoirNetworkReverse,
      label: 'Classification Requests',
      route: ROUTES.FULL_CLASSIFICATION_REQUESTS_ROUTE,
      exactRoute: true,
      roles: REQUESTS_ROLES,
    },
    {
      icon: IconoirPriorityUp,
      label: 'Expedite Requests',
      route: ROUTES.FULL_EXPEDITE_REQUESTS_ROUTE,
      exactRoute: true,
      roles: EXPEDITE_REQUESTS_ROLES,
    },
    {
      icon: IconoirFlash,
      label: 'Dispute Requests',
      route: ROUTES.FULL_DISPUTE_REQUESTS_ROUTE,
      exactRoute: true,
      roles: DISPUTE_REQUESTS_ROLES,
    },
    {
      icon: IconoirInfoCircle,
      label: 'Information Requests',
      route: ROUTES.FULL_INFORMATION_REQUESTS_ROUTE,
      exactRoute: true,
      roles: INFORMATION_REQUESTS_ROLES,
    },
    {
      icon: IconoirCurveArray,
      label: 'Audit History',
      route: ROUTES.FULL_AUDIT_HISTORY_ROUTE,
      exactRoute: true,
      roles: AUDIT_HISTORY_ROLES,
    },
  ];
  public theme: WritableSignal<THEME> = signal<THEME>(THEME.LIGHT);

  #authService: EytcAuthService = inject(EytcAuthService);
  #localStorageService: LocalStorageService = inject(LocalStorageService);
  #privacyNoticeService = inject(PrivacyNoticeService);
  #eventService: EventService = inject(EventService);
  #idleService: IdleService = inject(IdleService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #settingsService = inject(SettingsService);
  #faviconElement: HTMLLinkElement;

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.#faviconElement = this.document.querySelector('#appFavicon') as HTMLLinkElement;
    this.subscribeToThemeToggle().subscribe((theme: boolean): void => {
      this.theme.set(theme ? THEME.DARK : THEME.LIGHT);
    });

    this.listener = (e: MediaQueryListEvent) =>
      this.setFavicon(e.matches ? 'assets/favicon-light.ico' : 'assets/favicon-dark.ico');

    if (this.lightQuery.addEventListener) {
      this.lightQuery.addEventListener('change', this.listener);
    }
  }

  public subscribeToThemeToggle(): Observable<boolean> {
    return this.#eventService
      .getEventByKey(APP_EVENT_KEY.THEME_TOGGLE)
      .pipe(map((event: APP_EVENT): boolean => !!event.value));
  }

  public subscribeToCompanyChange(): Observable<CompanyContext> {
    return this.#eventService.getEventByKey(APP_EVENT_KEY.COMPANY_SELECTED).pipe(
      map((event: APP_EVENT): CompanyContext => {
        const selectedCompany = JSON.parse(event.value?.toString() ?? '{}');
        return selectedCompany as CompanyContext;
      })
    );
  }

  public isDarkTheme(): boolean {
    return this.theme() === THEME.DARK;
  }

  public initializeTheme(): void {
    // TODO save theme preferences in user config
    const storedTheme = this.#localStorageService.getItem('themePreference') as THEME;
    if (storedTheme) {
      this.theme.set(storedTheme);
    } else {
      this.theme.set(THEME.LIGHT);
    }
  }

  public subscribeToTimeOutEvents(): Observable<APP_EVENT> {
    return merge(
      this.#eventService.getDistinctEventByKeyValue(APP_EVENT_KEY.SHOW_LOGOUT_WARNING).pipe(
        pairwise(),
        filter(([prev, curr]: [APP_EVENT, APP_EVENT]): boolean => prev.value === false && curr.value === true),
        map(([, curr]: [APP_EVENT, APP_EVENT]): APP_EVENT => curr)
      ),
      this.#eventService.getEventByKey(APP_EVENT_KEY.LOGOUT),
      this.#eventService.getEventByKey(APP_EVENT_KEY.LOGOUT_BY_TIMEOUT)
    );
  }

  public handleTimeOutEvent(event: APP_EVENT): void {
    switch (event.key) {
      case APP_EVENT_KEY.LOGOUT_BY_TIMEOUT:
      case APP_EVENT_KEY.LOGOUT: {
        this.modal.closeAll();

        this.#authService.logout(event.key === APP_EVENT_KEY.LOGOUT_BY_TIMEOUT);
        this.#idleService.disableActivityListeners();
        break;
      }
      case APP_EVENT_KEY.SHOW_LOGOUT_WARNING: {
        const modalRef = this.modal.open(LogoutWarningModalComponent, {
          disableClose: true,
        });
        combineLatest([this.#settingsService.getAll(), modalRef.afterClosed()])
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe(([settings, result]) => {
            if (result) {
              const idleTimeout = settings.getNumberOrDefault(APP_TIMEOUT_SETTING_KEY, DEFAULT_APP_TIMEOUT);
              const warningTimeout = settings.getNumberOrDefault(
                APP_WARNING_TIMEOUT_SETTING_KEY,
                DEFAULT_APP_WARNING_TIMEOUT
              );
              this.#idleService.startIdleTimer(idleTimeout, warningTimeout);
            }
          });
        this.#idleService.disableActivityListeners();
        break;
      }
      default:
        break;
    }
  }

  public checkPrivacyNotice(
    forceShow = false,
    variant: 'acknowledge' | 'show' = 'acknowledge'
  ): Observable<boolean | undefined> {
    return this.#privacyNoticeService.getCurrent().pipe(
      filter(
        (privacyNotice: PrivacyNoticeDto | null): boolean =>
          !!privacyNotice && (!privacyNotice.acknowledged || forceShow)
      ),
      switchMap(privacyNotice => {
        const modalConfig: MotifModalConfig = {
          disableClose: true,
          size: variant == 'acknowledge' ? ModalSizes.TAKE_OVER : ModalSizes.XL,
          data: new PrivacyPolicyComponentData(privacyNotice!, variant),
        };

        const modalRef = this.modal.open<PrivacyPolicyComponent, MotifModalConfig, boolean>(
          PrivacyPolicyComponent,
          modalConfig
        );

        return modalRef.afterClosed();
      }),
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  setFavicon(iconUrl: string): void {
    if (this.#faviconElement) {
      this.#faviconElement.href = iconUrl;
    }
  }
}
